import { defineStore } from 'pinia';
import { paginateUrl } from './helpers';
import axios from 'axios';
import router from '../router';
import eventBus from '../eventBus';

export const useCreativesStore = defineStore('creatives', {
    state: () => ({
        creatives: []
    }),

    getters: {
        creative: (state) => {
            return (id) => state.creatives.find(creative => creative.id == id);
        },
    },

    actions: {
        _upsertCreative(payload) {
            const idx = this.creatives.findIndex(c => c.id === payload.id);
            if (idx !== -1) {
                this.creatives[idx] = payload;
            } else {
                this.creatives.push(payload);
            }
        },

        async readCreative(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id);
                this._upsertCreative(response.data.data);
                return response.data;
            } catch (error) {
                if (error.response?.status == 404) {
                    const idx = this.creatives.findIndex(c => c.id === payload.creative.id);
                    if (idx !== -1) this.creatives.splice(idx, 1);
                    eventBus.emit("SHOW_ALERT", { status: 404, data: { message: "This creative is no longer available" } });
                    await router.push('/creatives');
                    return;
                }
                throw error.response ?? error;
            }
        },

        async getPreviewUrl(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id + '/preview-url');
                return response.data;
            } catch (error) {
                if (error.response?.status == 404) {
                    const idx = this.creatives.findIndex(c => c.id === payload.creative.id);
                    if (idx !== -1) this.creatives.splice(idx, 1);
                    eventBus.emit("SHOW_ALERT", { status: 404, data: { message: "This creative is no longer available" } });
                    await router.push('/creatives');
                    return;
                }
                throw error.response ?? error;
            }
        },

        async getCreativeReportUrl(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id + '/report-url');
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async fetchApprovals(payload) {
            let token = payload.token;
            let status = payload.status;
            let url = `/v1/creatives/publisherapproval/${token}/approvals`;

            if (status === 'all') {
                url = url + '?status=all';
            }

            try {
                const response = await axios.get(url);
                return response.data.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async readCreatives(payload) {
            const requestUrl = paginateUrl('/v1/creatives/?', payload.pagination);
            try {
                const response = await axios.get(requestUrl);
                const creatives = response.data.data;
                creatives.forEach(creative => {
                    this._upsertCreative(creative);
                });
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async createCreative(payload) {
            try {
                const response = await axios.post("/v1/creatives", payload.creative);
                this.creatives.push(response.data.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async updateCreative(payload) {
            try {
                const response = await axios.put('/v1/creatives/' + payload.creative.id, payload.creative);
                this._upsertCreative(response.data.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async deleteCreative(payload) {
            try {
                const response = await axios.delete("/v1/creatives/" + payload.creative.id);
                const idx = this.creatives.findIndex(c => c.id === payload.creative.id);
                if (idx !== -1) this.creatives.splice(idx, 1);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async readPackages(payload) {
            const requestUrl = paginateUrl(`/v1/creatives/${payload.creative.id}/packages?`, payload.pagination);
            try {
                const response = await axios.get(requestUrl);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async createPackage(payload) {
            try {
                const response = await axios.post('/v1/creatives/' + payload.creative.id + '/packages', payload.producerPackage);
                return { data: response.data, status: response.status };
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async readPackage(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creativeId + '/packages/' + payload.packageId);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async updatePackage(payload) {
            try {
                const response = await axios.put('/v1/creatives/' + payload.creative.id + '/packages/' + payload.producerPackage.id, payload.producerPackage);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async getDynamicCode(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id + '/dynamic-code');
                return response.data;
            } catch (error) {
                if (error.response?.status == 404) {
                    const idx = this.creatives.findIndex(c => c.id === payload.creative.id);
                    if (idx !== -1) this.creatives.splice(idx, 1);
                    eventBus.emit("SHOW_ALERT", { status: 404, data: { message: "This creative is no longer available" } });
                    await router.push('/creatives');
                    return;
                }
                throw error.response ?? error;
            }
        },

        async setDynamicCode(payload) {
            try {
                const response = await axios.post('/v1/creatives/' + payload.creative.id + '/dynamic-code', payload.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async setDynamicCodeStatus(payload) {
            try {
                const response = await axios.post('/v1/creatives/' + payload.creative.id + '/dynamic-code/status', payload.data);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async setFallback(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id + '/fallback/' + payload.asset.id);
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },

        async readPreviewRules(payload) {
            try {
                const response = await axios.get('/v1/creatives/' + payload.creative.id + '/profiles/' + payload.profile.id + '/rules/ids');
                return response.data;
            } catch (error) {
                throw error.response ?? error;
            }
        },
    },

});
